/**
 * Landing and release lanes (docs/ux/project-tab-spec.md §1.5): one row for landing to main,
 * then one row per configured release lane with its trigger, newest build, the changes landed
 * since, and the next action. Each row opens the landing/lane inspector (`release:<id>`); the
 * action button runs the registry verb (lib/actions.ts `lane.build`).
 */
import type { ReactNode } from 'react'
import { type ActionEntity, actions } from '../../lib/actions.js'
import { useActionCtx } from '../../lib/entity-actions.js'
import { stackView } from '../../lib/landing-modes.js'
import type { LanesInput } from '../../lib/lanes-model.js'
import { type LaneLive, liveLabel, plural, type ReleaseRow } from '../../lib/release-lanes.js'
import type { Landing } from '../../lib/types.js'
import { StackBlock } from '../LandingModes.js'
import { Ago } from '../ui.js'
import { Glyph } from './glyphs.js'
import { useIsSelected } from './selection.js'
import './release-strip.css'

/** The registry entity for a release lane row (undefined for the landing row). */
export function laneEntity(r: ReleaseRow, projectId: string): ActionEntity | undefined {
  if (r.kind !== 'lane') return undefined
  return {
    kind: 'lane',
    id: r.id,
    projectId,
    title: r.name,
    ...(r.machineId ? { machineId: r.machineId } : {}),
    ...(r.production ? { production: true } : {}),
    ...(r.action ? { label: r.action.label } : {}),
  }
}

export function ReleaseStrip({
  rows,
  projectId,
  onInspect,
  aside,
}: {
  rows: ReleaseRow[]
  projectId: string
  onInspect: (key: string) => void
  /** The head's right-hand link (the tab passes "All builds g b"). */
  aside?: ReactNode
}) {
  if (!rows.length) return null
  return (
    <section className="lk-builds lk-rel" aria-labelledby="lk-builds-h">
      <div className="lk-section-h">
        <h2 id="lk-builds-h">Landing and release lanes</h2>
        {aside}
      </div>
      <ul className="lk-rel__list">
        {rows.map((r) => (
          <Row key={r.key} row={r} projectId={projectId} onInspect={onInspect} />
        ))}
      </ul>
    </section>
  )
}

function Row({
  row: r,
  projectId,
  onInspect,
}: {
  row: ReleaseRow
  projectId: string
  onInspect: (key: string) => void
}) {
  const selected = useIsSelected('release', r.id)
  const ctx = useActionCtx({ projectId })
  const entity = laneEntity(r, projectId)
  const act = r.action && entity ? actions(entity, ctx)[0] : undefined
  return (
    <li className={`lk-rel__row is-${r.state}${selected ? ' is-selected' : ''}`}>
      <button
        type="button"
        className="lk-rel__main"
        aria-current={selected ? 'true' : undefined}
        onClick={() => onInspect(r.key)}
      >
        <span className="lk-rel__name">
          <Glyph kind={r.state} />
          <span>{r.name}</span>
          <span className="lk-rel__policy">{r.policy}</span>
        </span>
        <span className="lk-rel__status">
          <span>{r.status}</span>
          {r.count ? <span className="lk-rel__count">{r.count.label}</span> : null}
          {r.note ? <span className="lk-rel__note">{r.note}</span> : null}
          {r.live ? (
            <span className="lk-rel__live" data-live={r.live.info.environment}>
              <span className="lk-rel__live-dot" aria-hidden="true" />
              {liveLabel(r.live)} on {r.live.info.environment} · since{' '}
              <Ago ts={r.live.info.since} />
            </span>
          ) : null}
        </span>
        <span className="lk-rel__when">
          <Ago ts={r.at} />
        </span>
      </button>
      {act ? (
        <button
          type="button"
          className="lk-rel__act"
          disabled={!!act.disabledReason}
          title={act.disabledReason ?? `${act.label} (${act.key})`}
          data-action={act.id}
          onClick={act.run}
        >
          {act.label}
        </button>
      ) : null}
    </li>
  )
}

// ---- inspector ----------------------------------------------------------------------------------

function Facts({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="lk-facts">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  )
}

const short = (sha?: string | null) => (sha ? sha.slice(0, 7) : '')
const LANDING_GLYPH: Record<string, 'ok' | 'run' | 'wait' | 'fail' | 'idle'> = {
  landed: 'ok',
  running: 'run',
  queued: 'run',
  awaiting_approval: 'wait',
  failed: 'fail',
  interrupted: 'fail',
}

function LandingList({
  list,
  onInspect,
  empty = 'None.',
}: {
  list: Landing[]
  onInspect: (k: string) => void
  empty?: string
}) {
  if (!list.length) return <p className="lk-muted">{empty}</p>
  return (
    <ul className="lk-steps">
      {list.map((l) => (
        <li key={l.id}>
          <Glyph kind={LANDING_GLYPH[l.state] ?? 'idle'} />
          <button
            type="button"
            className="lk-link !no-underline"
            onClick={() => onInspect(`landing:${l.id}`)}
          >
            {l.summary ?? l.branch ?? l.id}
          </button>
          <span className="lk-rel__meta">
            {l.state === 'landed'
              ? short(l.commit)
              : l.state === 'failed'
                ? (l.reason ?? l.error ?? 'failed')
                : (l.step ?? l.state.replace('_', ' '))}{' '}
            · <Ago ts={l.updatedAt} />
          </span>
        </li>
      ))}
    </ul>
  )
}

/** `release:<id>` inspector body: the landing row or one release lane. */
export function releaseInspector(
  id: string,
  model: { projectId: string; builds: ReleaseRow[] },
  input: Pick<LanesInput, 'landings' | 'machines'>,
  onInspect: (k: string) => void,
): { title: ReactNode; sub?: ReactNode; content: ReactNode; entity?: ActionEntity } | undefined {
  const r = model.builds.find((x) => x.id === decodeURIComponent(id))
  if (!r) return undefined
  const h3 = (t: string) => <h3 className="lk-rel__h3">{t}</h3>
  if (r.kind === 'landing') {
    const recent = input.landings
      .filter((l) => l.projectId === model.projectId)
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, 12)
    return {
      title: r.name,
      sub: `${r.policy}. ${r.status}.`,
      content: (
        <>
          <Facts
            rows={[
              ['Policy', r.policy],
              [
                'Last landed',
                r.landed ? (
                  <button
                    key="l"
                    type="button"
                    className="lk-link"
                    onClick={() => onInspect(`landing:${r.landed?.id}`)}
                  >
                    {short(r.landed.commit) || r.landed.branch} · {r.landed.branch}
                  </button>
                ) : (
                  'Nothing yet'
                ),
              ],
              ['In flight', r.landings.length ? plural(r.landings.length, 'landing') : 'None'],
              [
                'Failing',
                r.failures.length ? plural(r.failures.length, 'branch', 'branches') : 'None',
              ],
            ]}
          />
          {(() => {
            const withStack = recent.find((l) => stackView(input.landings, l))
            const st = withStack ? stackView(input.landings, withStack) : undefined
            return st ? (
              <>
                {h3('Stacked landing')}
                <StackBlock stack={st} onOpen={(id) => onInspect(`landing:${id}`)} />
              </>
            ) : null
          })()}
          {h3('In flight')}
          <LandingList list={r.landings} onInspect={onInspect} empty="Nothing is landing now." />
          {r.failures.map((f) => (
            <div key={f.branch} className="grid gap-2">
              {h3(`${f.branch}: newest attempt failed`)}
              <LandingList list={[f.latest, ...f.earlier]} onInspect={onInspect} />
            </div>
          ))}
          {h3('Recent attempts')}
          <LandingList list={recent} onInspect={onInspect} />
        </>
      ),
    }
  }
  const latest = r.builds[0]
  const machine = input.machines.find((m) => m.id === r.machineId)
  const entity = laneEntity(r, model.projectId)
  const live = r.live
  return {
    ...(entity ? { entity } : {}),
    title: r.name,
    sub: `${r.policy}. ${r.status}.`,
    content: (
      <>
        <Facts
          rows={[
            ['Trigger', r.policy],
            ['Target', r.lane?.target ?? 'custom'],
            ['Machine', machine?.name ?? r.lane?.machine ?? 'Not enrolled'],
            [
              'Latest build',
              latest ? (
                <button
                  key="b"
                  type="button"
                  className="lk-link"
                  onClick={() => onInspect(`build:${latest.buildId}`)}
                >
                  {latest.version ?? latest.buildId} · {latest.status}
                </button>
              ) : (
                'No build yet'
              ),
            ],
            ...(r.ship
              ? ([
                  [
                    'Waiting',
                    `${r.ship.gate === 'step-up' ? 'A passkey tap' : 'Your tap'} to ship ${plural(r.ship.landings, 'landing')}`,
                  ],
                ] as [string, ReactNode][])
              : []),
            ...(latest?.failure
              ? ([
                  ['Failure', latest.failure.cause ?? 'failed'],
                  ...(latest.failure.fix ? [['Fix', latest.failure.fix]] : []),
                  ...(latest.failure.evidence
                    ? [['Evidence', <code key="e">{latest.failure.evidence}</code>]]
                    : []),
                ] as [string, ReactNode][])
              : []),
          ]}
        />
        {live ? <WhatsLive live={live} onInspect={onInspect} /> : null}
        {h3(
          latest
            ? `Changes since the last build (${r.landings.length})`
            : `Changes ready (${r.landings.length})`,
        )}
        <LandingList
          list={r.landings}
          onInspect={onInspect}
          empty="Nothing landed since the last build."
        />
        {h3(`Builds (${r.builds.length})`)}
        {r.builds.length ? (
          <ul className="lk-steps">
            {r.builds.slice(0, 12).map((b) => (
              <li key={b.buildId}>
                <Glyph
                  kind={b.status === 'available' ? 'ok' : b.status === 'failed' ? 'fail' : 'run'}
                />
                <button
                  type="button"
                  className="lk-link !no-underline"
                  onClick={() => onInspect(`build:${b.buildId}`)}
                >
                  {b.version ?? b.buildId}
                </button>
                <span className="lk-rel__meta">
                  {b.status} · <Ago ts={b.updatedAt ?? b.createdAt ?? undefined} />
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="lk-muted">
            This lane has not built yet.{' '}
            {r.lane?.trigger.kind === 'on-land' && !r.production
              ? r.policy.includes('tap')
                ? 'The next landing asks for your tap to build it, or start one below.'
                : 'The next landing builds it.'
              : 'Start one with the action below.'}
          </p>
        )}
      </>
    ),
  }
}

/** "What's live" for one lane/environment: drillable proof, build, commit and shipped tasks. */
export function WhatsLive({ live, onInspect }: { live: LaneLive; onInspect: (k: string) => void }) {
  const { info, build } = live
  const sha = info.commit.slice(0, 7)
  return (
    <section className="lk-rel__whatslive" aria-label={`What's live on ${info.environment}`}>
      <h3 className="lk-rel__h3">What's live on {info.environment}</h3>
      <Facts
        rows={[
          ['Version', info.version],
          [
            'Commit',
            live.landing ? (
              <button
                key="c"
                type="button"
                className="lk-link"
                onClick={() => onInspect(`landing:${live.landing?.id}`)}
              >
                <code>{sha}</code> ·{' '}
                {live.landing.summary ?? live.landing.branch ?? 'landed change'}
              </button>
            ) : (
              <code key="c" title={info.commit}>
                {info.commit}
              </code>
            ),
          ],
          ['Live since', <Ago key="s" ts={info.since} />],
          [
            'Proof',
            <span key="p">
              {info.source === 'verify' ? 'Verify passed' : 'Available in the store'}
              {info.detail ? `: ${info.detail}` : ''} · <Ago ts={info.verifiedAt} />
            </span>,
          ],
          ...(info.url
            ? ([
                [
                  'URL',
                  <a key="u" className="lk-link" href={info.url} target="_blank" rel="noreferrer">
                    {info.url}
                  </a>,
                ],
              ] as [string, ReactNode][])
            : []),
          [
            'Build',
            <button
              key="b"
              type="button"
              className="lk-link"
              onClick={() => onInspect(`build:${build.buildId}`)}
            >
              {build.title}
            </button>,
          ],
          ...(live.newer
            ? ([
                [
                  'Newer build',
                  <button
                    key="n"
                    type="button"
                    className="lk-link"
                    onClick={() => onInspect(`build:${live.newer?.buildId}`)}
                  >
                    {live.newer.version ?? live.newer.buildId} · {live.newer.status}
                  </button>,
                ],
              ] as [string, ReactNode][])
            : []),
        ]}
      />
      <h3 className="lk-rel__h3">Tasks in what's live ({build.tasks.length})</h3>
      {build.tasks.length ? (
        <ul className="lk-steps">
          {build.tasks.map((t, i) => (
            <li key={t.taskId ?? `${t.title}-${i}`}>
              <Glyph kind="ok" />
              {t.taskId ? (
                <button
                  type="button"
                  className="lk-link !no-underline"
                  onClick={() => onInspect(`task:${t.taskId}`)}
                >
                  {t.title}
                </button>
              ) : (
                <span>{t.title}</span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="lk-muted">No task list for this build.</p>
      )}
    </section>
  )
}
